import * as React from "react";
import { Radio, RadioGroup as AriakitRadioGroup, useRadioStore } from "@ariakit/react";
import { cn } from "@/lib/utils";

interface RadioGroupProps {
  value?: string;
  defaultValue?: string;
  onValueChange?: (value: string) => void;
  className?: string;
  children: React.ReactNode;
}

const RadioGroup = React.forwardRef<HTMLDivElement, RadioGroupProps>(
  ({ className, value, defaultValue, onValueChange, children, ...props }, ref) => {
    const store = useRadioStore({ 
      value, 
      defaultValue,
      setValue: onValueChange ? (value: string | number | null) => onValueChange(value as string) : undefined,
    });

    return (
      <AriakitRadioGroup
        ref={ref}
        store={store}
        className={cn("grid gap-2", className)}
        {...props}
      >
        {children}
      </AriakitRadioGroup>
    );
  }
);
RadioGroup.displayName = "RadioGroup";

interface RadioGroupItemProps {
  value: string;
  id?: string;
  className?: string;
  disabled?: boolean;
}

const RadioGroupItem = React.forwardRef<HTMLInputElement, RadioGroupItemProps>(
  ({ className, value, id, disabled, ...props }, ref) => {
    return (
      <Radio
        ref={ref}
        value={value}
        id={id}
        disabled={disabled}
        className={cn(
          "aspect-square h-4 w-4 rounded-full border border-primary text-primary ring-offset-background",
          "focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
          "disabled:cursor-not-allowed disabled:opacity-50",
          "data-[checked]:bg-primary data-[checked]:border-primary",
          "relative after:absolute after:inset-0 after:m-auto after:h-2 after:w-2 after:rounded-full",
          "data-[checked]:after:bg-primary-foreground",
          className
        )}
        {...props}
      />
    );
  }
);
RadioGroupItem.displayName = "RadioGroupItem";

export { RadioGroup, RadioGroupItem };
